import React, { useCallback, useEffect } from 'react';
import { useAuth } from '../../../../auth/context/AuthProvider';
import useShowBooking from '../../../../services/booking/hooks/useShowBooking';
import { useForm } from 'react-hook-form';
import { ChangeCommentForm, ChangeCommentSchema } from '../../schema/ChangeCommentSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import FormError from '../../../../shared/components/errors/FormError';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';
import { Button } from 'react-bootstrap';
// --- NEW --- Import the new hook
import { useUpdateBookingComment } from '../../../../services/create-booking-v4/hooks/useUpdateBookingCommentV4';

interface ChangeCommentFormComponentProps {
  onClose: () => void;
  reservationId: string;
}

const ChangeCommentFormComponent: React.FC<ChangeCommentFormComponentProps> = ({ onClose, reservationId }) => {
  const { hasRole, hasPermission } = useAuth();
  const { data: reservation, isFetching, isSuccess, isError } = useShowBooking({
    bookingId: reservationId
  });

  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm<ChangeCommentForm>({
    resolver: zodResolver(ChangeCommentSchema),
  });

  // Effect to populate form with initial data
  useEffect(() => {
    if (isSuccess && reservation) {
      reset({
        public_note: reservation.data.public_note,
        private_note: reservation.data.private_note,
      });
    }
  }, [isSuccess, reservation, reset]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // --- NEW --- Use the new hook for updating comments
  const { mutate, isPending } = useUpdateBookingComment();

  const queryClient = useQueryClient();

  // --- UPDATED --- The onSubmit function is now much simpler
  const onSubmit = useCallback((values: ChangeCommentForm) => {
    if (!hasRole('dev') && !hasPermission('booking_update')) {
      toast.error("You don't have permission to do this action");
      return;
    }

    toast.loading('Saving comments...', {
      id: 'change-comment'
    });

    // The payload is now just the form values, which matches the new hook's expectation.
    mutate({ bookingId: reservationId, formData: values }, {
      onSuccess: async () => {
        handleClose();

        // Refetch queries for this specific booking and any lists.
        await queryClient.invalidateQueries({ queryKey: ['booking-show', reservationId] });
        await queryClient.invalidateQueries({ queryKey: ['bookings'] });

        toast.success('Comments changed successfully', { id: 'change-comment' });
      },
      onError: (mutationError: any) => {
        // Keep the existing robust error handling
        if (mutationError?.response?.status === 422) {
          const validationErrors = mutationError.response.data.errors;
          if (validationErrors) {
            for (const [field, messages] of Object.entries(validationErrors)) {
              setError(field as keyof ChangeCommentForm, {
                type: "manual",
                message: (messages as string[])[0],
              });
            }
            toast.error('Failed to change comments. Please check the errors.', { id: 'change-comment' });
          }
        } else {
          toast.error(mutationError.message || 'An unexpected error occurred.', { id: 'change-comment' });
        }
      },
    });

  }, [hasRole, hasPermission, reservationId, mutate, handleClose, queryClient, setError]);

  return (
    <>
      {isError && <FormError />}
      {isFetching && <FormSpinner />}
      {isSuccess && reservation && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='form-group mb-3'>
            <label htmlFor='public_note'>Public Note</label>
            <textarea
              {...register('public_note')}
              className='form-control'
              id='public_note'
              rows={4}
            />
            {errors.public_note && <small className='text-danger'>{errors.public_note.message}</small>}
          </div>
          <div className='form-group mb-3'>
            <label htmlFor='private_note'>Private Note</label>
            <textarea
              {...register('private_note')}
              className='form-control'
              id='private_note'
              rows={4}
            />
            {errors.private_note && <small className='text-danger'>{errors.private_note.message}</small>}
          </div>
          <Button className='mt-3' type='submit' disabled={isPending}>
            {isPending ? 'Submitting...' : 'Submit'}
          </Button>
        </form>
      )}
    </>
  );
};

export default ChangeCommentFormComponent;